import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, CheckCircle, AlertCircle } from 'lucide-react';
import api from '../utils/axios';
import toast from 'react-hot-toast';

const FileUpload = ({ 
  onUploadSuccess, 
  onUploadError, 
  multiple = false, 
  maxFiles = 5,
  acceptedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
  maxSize = 5 * 1024 * 1024, // 5MB
  className = '',
  disabled = false,
  showPreview = true,
  placeholder = 'Click to upload or drag and drop',
  existingImageUrl = null
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFiles = async (files) => {
    const fileArray = Array.from(files);
    
    // Validate file types
    const invalidFiles = fileArray.filter(file => !acceptedTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      toast.error(`Invalid file type(s): ${invalidFiles.map(f => f.name).join(', ')}`);
      return;
    }

    // Validate file sizes
    const oversizedFiles = fileArray.filter(file => file.size > maxSize);
    if (oversizedFiles.length > 0) {
      toast.error(`File(s) too large: ${oversizedFiles.map(f => f.name).join(', ')}`);
      return;
    }

    // Check max files limit
    if (!multiple && fileArray.length > 1) {
      toast.error('Only one file allowed');
      return;
    }

    if (multiple && fileArray.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} files allowed`);
      return;
    }

    setIsUploading(true);
    
    try {
      const formData = new FormData();
      fileArray.forEach(file => {
        formData.append(multiple ? 'images' : 'image', file);
      });

      const endpoint = multiple ? '/api/upload/multiple' : '/api/upload/single';
      
      // Get the token for authentication
      const token = localStorage.getItem('vendorToken');
      if (!token) {
        throw new Error('Authentication required. Please login again.');
      }

      const response = await api.post(endpoint, formData);
      // Note: Authorization header and Content-Type are handled by axios interceptor

      const newFiles = multiple ? response.data.files : [response.data.file];
      setUploadedFiles(prev => [...prev, ...newFiles]);
      
      toast.success('File(s) uploaded successfully!');
      
      if (onUploadSuccess) {
        onUploadSuccess(multiple ? newFiles : newFiles[0]);
      }
    } catch (error) {
      console.error('Upload error:', error);
      
      // Handle different types of errors
      let errorMessage = 'Upload failed';
      
      if (error.message && error.message.includes('Authentication required')) {
        errorMessage = error.message;
        // Redirect to login if authentication is required
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else if (error.response) {
        // Server responded with error status
        if (error.response.status === 401) {
          errorMessage = 'Session expired. Please login again.';
          localStorage.removeItem('vendorToken');
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
        } else if (error.response.status === 413) {
          errorMessage = 'File too large. Please try a smaller file.';
        } else if (error.response.status === 400) {
          errorMessage = error.response.data?.message || 'Invalid file or request.';
        } else {
          errorMessage = error.response.data?.message || `Server error: ${error.response.status}`;
        }
      } else if (error.request) {
        // Network error
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      toast.error(errorMessage);
      
      if (onUploadError) {
        onUploadError(error);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive 
            ? 'border-primary-500 bg-primary-50' 
            : 'border-secondary-300 hover:border-primary-400'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={!disabled ? openFileDialog : undefined}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          accept={acceptedTypes.join(',')}
          onChange={handleFileInput}
          className="hidden"
          disabled={disabled || isUploading}
        />
        
        <div className="space-y-2">
          <Upload className={`mx-auto h-8 w-8 ${dragActive ? 'text-primary-500' : 'text-secondary-400'}`} />
          <p className="text-sm text-secondary-600">{placeholder}</p>
          <p className="text-xs text-secondary-500">
            {acceptedTypes.map(type => type.split('/')[1]).join(', ').toUpperCase()} • Max {Math.round(maxSize / 1024 / 1024)}MB
            {multiple && ` • Max ${maxFiles} files`}
          </p>
        </div>

        {isUploading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500"></div>
              <span className="text-sm text-secondary-600">Uploading...</span>
            </div>
          </div>
        )}
      </div>

      {/* Existing Image Preview */}
      {showPreview && existingImageUrl && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-secondary-700">Current Image:</h4>
          <div className="relative group">
            <div className="border border-secondary-200 rounded-lg p-3 bg-secondary-50">
              <div className="flex items-center space-x-2 mb-2">
                <ImageIcon className="h-4 w-4 text-secondary-400" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-secondary-900">Current Image</p>
                  <p className="text-xs text-secondary-500">Will be replaced when you upload a new image</p>
                </div>
                <CheckCircle className="h-4 w-4 text-blue-500" />
              </div>
              
              <img
                src={existingImageUrl}
                alt="Current image"
                className="w-full h-20 object-cover rounded"
              />
            </div>
          </div>
        </div>
      )}

      {/* File Preview */}
      {showPreview && uploadedFiles.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-secondary-700">New Uploaded Files:</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {uploadedFiles.map((file, index) => (
              <div key={index} className="relative group">
                <div className="border border-secondary-200 rounded-lg p-3 bg-secondary-50">
                  <div className="flex items-center space-x-2">
                    <ImageIcon className="h-4 w-4 text-secondary-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-secondary-900 truncate">
                        {file.originalname || file.filename}
                      </p>
                      <p className="text-xs text-secondary-500">
                        {Math.round((file.size || 0) / 1024)}KB
                      </p>
                    </div>
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  </div>
                  
                  {file.url && (
                    <img
                      src={file.url}
                      alt={file.originalname || file.filename}
                      className="mt-2 w-full h-20 object-cover rounded"
                    />
                  )}
                </div>
                
                <button
                  onClick={() => removeFile(index)}
                  className="absolute -top-2 -right-2 h-6 w-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload; 